import React, { useState } from 'react';

import { updateImageSize } from '../../modules/getDealImage/updateImageSize';
import getImageUrl from '../../modules/getImageUrl';
import type { Deal } from '../../types/Deal';
import type { TabletopBlockRow } from '../../types/TabletopBlockRow';
import { useHawkWidgetContext } from '../GenericComponents/HawkWidget/HawkWidgetProviderContext';
import LazyImage from '../GenericComponents/LazyLoad/LazyImage';
import StarRating from '../GenericComponents/StarRating/StarRating';

import styles from './styles/tabletop-block.css';
import TabletopOfferCta from './TabletopOfferCta';

type TabletopBlockProps = {
  row: TabletopBlockRow;
  onReadMoreClick: (offerId?: number) => void;
};

const TabletopBlock: React.FC<TabletopBlockProps> = ({ row, onReadMoreClick }) => {
  const { translate } = useHawkWidgetContext();
  const { buyingGuideBlock: block, modelName, deals, groupModelImageUrl } = row;
  const [activeButton, setActiveButton] = useState(0);

  const { navigationLink, starRating, productName, productHeader, pros, imageUrl } = block;

  const firstDeal = deals[0] as Deal | undefined;
  const offerImage = firstDeal?.image ?? firstDeal?.model_image;
  const imageSrc =
    groupModelImageUrl ?? offerImage ?? imageUrl ?? getImageUrl('misc/no-image-available.svg');

  const imageWidth = 140;

  return (
    <div className={styles['block']}>
      <div className={styles['image-container']}>
        <LazyImage
          src={updateImageSize(imageSrc, 2 * imageWidth)}
          alt={modelName}
          attributes={{
            'data-area-clicked': 'deal-image',
          }}
          width={imageWidth}
          height={80}
        />
      </div>
      <div className={styles['product-container']}>
        <span className={styles['product-header']}>{productHeader}</span>
        <span className={styles['product-name']}>{productName}</span>
        {Number(starRating) > 0 && (
          <span>
            <StarRating className="tabletop" score={Number(starRating) / 10} />
          </span>
        )}
        {pros?.[0] && <span className={styles['pro']}>{pros[0]}</span>}
        {navigationLink && (
          <a
            href={navigationLink}
            className={styles['read-more']}
            onClick={(): void => onReadMoreClick(firstDeal?.id)}
          >
            {translate('readMore')}
            <span className={styles['read-more-arrow']}>▼</span>
          </a>
        )}
      </div>
      <div
        className={styles['offers-container']}
        onMouseLeave={(): void => {
          setActiveButton(0);
        }}
      >
        {deals.map((deal, index) => (
          <TabletopOfferCta
            key={`${deal.id}-${deal.offer.link}`}
            deal={deal}
            activeButton={activeButton}
            setActiveButton={setActiveButton}
            index={index}
            transformToUppercase={true}
          />
        ))}
      </div>
    </div>
  );
};

export default TabletopBlock;
